import { Alert, Button, Card, StatusPill } from "@breenova/ui";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { DetailHeader } from "@/components/app-shell";
import { useCreateNest, useNests, type NestSummary } from "@/lib/breeding";

const NEST_TONES = {
  Building: "neutral",
  Laying: "biological",
  Incubating: "biological",
  Hatching: "positive",
  Fledged: "positive",
  Failed: "critical",
  Unknown: "neutral",
} as const;

/** The rounds a pair has produced this season, newest first. */
export function PairRoundsPage() {
  const { id } = useParams<{ id: string }>();
  const nests = useNests(id);
  const createNest = useCreateNest();
  const [error, setError] = useState<string | null>(null);

  async function startRound(): Promise<void> {
    setError(null);

    try {
      // No round number sent: the server assigns one past the highest already recorded,
      // so two taps cannot both claim the same round.
      await createNest.mutateAsync({ pairId: id! });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start the round.");
    }
  }

  const rounds = nests.data?.nests ?? [];

  return (
    <div className="flex flex-col gap-md">
      <DetailHeader
        backTo="/breeding"
        kicker={rounds.length > 0 ? `${rounds.length} this season` : "This pair"}
        title="Rounds"
        action={
          <Button size="sm" onClick={() => void startRound()} disabled={createNest.isPending}>
            <Plus className="size-4" />
            {createNest.isPending ? "Starting…" : "Start"}
          </Button>
        }
      />

      <section className="flex flex-col gap-md px-5 pb-lg md:px-gutter-wide">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {nests.isPending ? (
          <p className="text-body text-on-surface-variant">Loading rounds…</p>
        ) : rounds.length === 0 ? (
          <Card className="flex flex-col items-center gap-sm p-xl text-center">
            <p className="text-card-title text-on-surface">No rounds yet</p>
            <p className="text-body text-on-surface-variant">
              Start a round when the hen begins building.
            </p>
          </Card>
        ) : (
          <ul
            // A round card is three figures and a status pill. Two or three across is what
            // lets a breeder compare this season's rounds without scrolling between them.
            className="grid grid-cols-1 gap-md md:grid-cols-2 xl:grid-cols-3"
          >
            {rounds.map((nest) => (
              <li key={nest.id}>
                <RoundCard nest={nest} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function RoundCard({ nest }: { nest: NestSummary }) {
  return (
    <Link to={`/breeding/nests/${nest.id}`} className="block">
      <Card className="flex flex-col gap-sm p-md transition-colors hover:bg-surface-container-low">
        <div className="flex items-center justify-between gap-sm">
          <p className="text-card-title text-on-surface">Round {nest.round}</p>
          <StatusPill tone={NEST_TONES[nest.status as keyof typeof NEST_TONES] ?? "neutral"}>
            {nest.status}
          </StatusPill>
        </div>

        <dl className="grid grid-cols-3 gap-sm text-on-surface" data-numeric>
          <div className="flex flex-col gap-xs">
            <dt className="text-label-caps uppercase text-on-surface-variant">Eggs</dt>
            <dd className="text-body">{nest.eggCount}</dd>
          </div>
          <div className="flex flex-col gap-xs">
            <dt className="text-label-caps uppercase text-on-surface-variant">Chicks</dt>
            <dd className="text-body">{nest.chickCount}</dd>
          </div>
          <div className="flex flex-col gap-xs">
            <dt className="text-label-caps uppercase text-on-surface-variant">Due</dt>
            <dd className="text-body">{nest.expectedHatchOn ?? "—"}</dd>
          </div>
        </dl>
      </Card>
    </Link>
  );
}
